// Typed API client.
//
// One place that knows how to talk to the server, so a change to an endpoint
// is a change to one file rather than a hunt through components.
//
// Session handling is deliberately invisible here: the browser carries an
// httpOnly cookie that JavaScript cannot read, so there is no token to attach
// and no token for an XSS to steal. `credentials: 'same-origin'` is the whole
// of it.

import type { AnswerValue, Answers, Outstanding, Progress, Template } from '@myshoebox/shared';

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Session expired or never existed. Callers send the user to sign in. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * Worth retrying: a network blip, a 5xx, or a rate limit.
   *
   * Matters for autosave — a 400 means the request was wrong and retrying
   * forever is pointless, while a dropped connection on a train should be
   * retried indefinitely.
   */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      headers: {
        // Only when there is actually a JSON body to describe. Declaring
        // application/json on a POST with no body makes Fastify reject it
        // with "Body cannot be empty" before the handler ever runs — which
        // is correct of Fastify, and was this client lying about what it
        // was sending.
        ...(init.body !== undefined && !(init.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...init.headers,
      },
    });
  } catch {
    // fetch only rejects for network-level failures. Status 0 marks it as
    // retryable without inventing a fake HTTP code.
    throw new ApiError(0, 'No connection. We will keep trying.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }

  if (!response.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Something went wrong (${response.status}).`;
    throw new ApiError(response.status, message);
  }

  return body as T;
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export interface ClientSessionInfo {
  kind: 'client';
  user: { id: string; email: string; name: string };
  client: { id: string; name: string; entityType: string };
}

export interface StaffSessionInfo {
  kind: 'staff';
  staff: {
    id: string;
    email: string;
    name: string;
    role: string;
    /** Only one account has this. It is the sole role that may appoint
     *  administrators, and the console hides that action for everyone else. */
    isSuperUser: boolean;
  };
}

export type SessionInfo = ClientSessionInfo | StaffSessionInfo;

// ---------------------------------------------------------------------------
// Second factor
// ---------------------------------------------------------------------------

/**
 * What the password step returns.
 *
 * A union, not an object with optional fields, so a caller cannot read
 * `staff.name` on a response that has none — the compiler makes it check
 * which case it is holding first.
 */
export type StaffSignInResult =
  | { staff: { name: string; role: string }; needsSecondFactor?: undefined }
  | { needsSecondFactor: true; challenge: string };

export interface StaffVerifyResult {
  staff: { name: string; role: string };
  /** Present when a recovery code was spent rather than a TOTP code used. */
  usedRecoveryCode?: boolean;
  recoveryCodesLeft?: number;
}

export interface MfaStatus {
  enrolled: boolean;
  enrolledAt: string | null;
  recoveryCodesLeft: number;
}

export interface MfaOffer {
  /** For the QR code. */
  otpauthUri: string;
  /** The same secret, grouped in fours for somebody typing it in. */
  secret: string;
}

export const api = {
  session: () => request<SessionInfo>('/api/auth/session'),

  requestSignInLink: (email: string) =>
    request<{ ok: true; message: string }>('/api/auth/request-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  /**
   * Firm sign-in, step one. Clients never call this — they have no password.
   *
   * The server answers identically for an unknown email and a wrong
   * password, so the caller must not try to be more helpful than it is.
   *
   * A correct password does NOT always mean a session. When the account has
   * a second factor, this resolves with `needsSecondFactor` and a challenge
   * token, and no cookie has been set — see staffVerify.
   */
  staffSignIn: (email: string, password: string) =>
    request<StaffSignInResult>('/api/auth/staff/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  /**
   * Firm sign-in, step two: the code from the authenticator app, or one of
   * the recovery codes.
   *
   * The challenge token comes from staffSignIn and is worthless on its own —
   * nothing else in the API accepts it.
   */
  staffVerify: (challenge: string, code: string) =>
    request<StaffVerifyResult>('/api/auth/staff/verify', {
      method: 'POST',
      body: JSON.stringify({ challenge, code }),
    }),

  signOut: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  // -------------------------------------------------------------------------
  // Your own second factor
  // -------------------------------------------------------------------------
  //
  // All four act on the signed-in staff member and nobody else. There is no
  // "reset it for this person" call, deliberately: a support path that turns
  // the second factor off on request is softer than the factor itself.

  mfaStatus: () => request<MfaStatus>('/api/practice/mfa'),

  /** Generate a secret and return it as a QR code and a typable key. */
  mfaBegin: () => request<MfaOffer>('/api/practice/mfa/begin', { method: 'POST' }),

  /** Prove the app holds the secret. Returns the recovery codes, once. */
  mfaConfirm: (code: string) =>
    request<{ recoveryCodes: string[] }>('/api/practice/mfa/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /** Turn it off. Needs a current code, not merely a session. */
  mfaDisable: (code: string) =>
    request<{ ok: true }>('/api/practice/mfa/disable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  // -------------------------------------------------------------------------
  // Firm console
  // -------------------------------------------------------------------------

  /**
   * Every client and return, for the console grid.
   *
   * Returns the whole set in one request; filtering and sorting happen in the
   * browser so they apply instantly. Note what is NOT here: TFNs and dates of
   * birth, only whether they exist.
   */
  practiceClients: (year?: number) =>
    request<PracticeClientsResponse>(
      year ? `/api/practice/clients?year=${year}` : '/api/practice/clients',
    ),

  /** Invite a client. An email address is the only required field. */
  addClient: (input: {
    email: string;
    // `| undefined` because exactOptionalPropertyTypes distinguishes "absent"
    // from "present and undefined", and the caller builds this from form
    // fields that are legitimately empty.
    name?: string | undefined;
    clientCode?: string | undefined;
    year?: number | undefined;
  }) =>
    request<AddClientResponse>('/api/practice/clients', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  invitations: () => request<{ invitations: Invitation[] }>('/api/practice/invitations'),

  resendInvitation: (clientUserId: string) =>
    request<{ ok: true; emailSent: boolean; expiresAt: string }>(
      `/api/practice/invitations/${clientUserId}/resend`,
      { method: 'POST' },
    ),

  cancelInvitation: (clientUserId: string) =>
    request<{ ok: true }>(`/api/practice/invitations/${clientUserId}/cancel`, {
      method: 'POST',
    }),

  restoreInvitation: (clientUserId: string) =>
    request<{ ok: true }>(`/api/practice/invitations/${clientUserId}/restore`, {
      method: 'POST',
    }),

  /**
   * Complete a client’s identity on their behalf.
   *
   * The TFN and date of birth are validated in the browser first as a
   * courtesy, and again on the server, which is the control.
   */
  updateClientIdentity: (clientId: string, fields: Record<string, string>) =>
    request<{ ok: true }>(`/api/practice/clients/${clientId}/identity`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }),

  practiceStaff: () =>
    request<{ staff: { id: string; name: string; role: string }[] }>('/api/practice/staff'),

  // -------------------------------------------------------------------------
  // Years
  // -------------------------------------------------------------------------

  /** The client’s own details. Never includes the tax file number itself. */
  profile: () => request<ClientProfile>('/api/me/profile'),

  updateProfile: (fields: Record<string, string>) =>
    request<{ ok: true }>('/api/me/profile', {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }),

  years: () => request<YearsResponse>('/api/me/years'),

  year: (year: number) => request<QuestionnaireBundle>(`/api/me/years/${year}`),

  saveAnswers: (year: number, answers: Record<string, AnswerValue>) =>
    request<SaveResponse>(`/api/me/years/${year}/answers`, {
      method: 'PUT',
      body: JSON.stringify({ answers }),
    }),

  submit: (year: number) =>
    request<{ ok: true; status: string }>(`/api/me/years/${year}/submit`, { method: 'POST' }),

  // -------------------------------------------------------------------------
  // Attachments
  // -------------------------------------------------------------------------

  uploadLimits: () => request<UploadLimits>('/api/me/upload-limits'),

  /**
   * Upload files against a question.
   *
   * `onProgress` is driven by XMLHttpRequest rather than fetch, because fetch
   * still cannot report upload progress. On a phone sending eight photos over
   * mobile data, a progress bar is the difference between waiting and
   * assuming it has broken.
   */
  uploadAttachments: (
    year: number,
    questionCode: string | null,
    files: File[],
    onProgress?: (fraction: number) => void,
  ): Promise<UploadResponse> => {
    const form = new FormData();
    if (questionCode) form.append('questionCode', questionCode);
    for (const file of files) form.append('files', file);

    return new Promise<UploadResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/me/years/${year}/attachments`);
      xhr.withCredentials = true;

      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) onProgress(event.loaded / event.total);
        });
      }

      xhr.addEventListener('load', () => {
        try {
          const body = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) resolve(body as UploadResponse);
          else reject(new ApiError(xhr.status, body?.error ?? 'Upload failed.'));
        } catch {
          reject(new ApiError(xhr.status, 'Upload failed.'));
        }
      });
      xhr.addEventListener('error', () =>
        reject(new ApiError(0, 'No connection. Please try again.')),
      );
      xhr.addEventListener('abort', () => reject(new ApiError(0, 'Upload cancelled.')));

      xhr.send(form);
    });
  },

  deleteAttachment: (id: string) =>
    request<{ ok: true }>(`/api/me/attachments/${id}`, { method: 'DELETE' }),

  /** Download URL for an attachment. The cookie authorises it. */
  attachmentUrl: (id: string) => `/api/attachments/${id}/download`,
};

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

export interface YearSummary {
  year: number;
  title: string;
  status: QuestionnaireStatus;
  submittedAt: string | null;
  updatedAt: string | null;
  answeredCount: number;
  attachmentCount: number;
}

export interface YearsResponse {
  years: YearSummary[];
  currentYear: number;
}

export type QuestionnaireStatus =
  | 'not_started'
  | 'in_progress'
  | 'submitted'
  | 'in_review'
  | 'queries'
  | 'complete';

export interface AttachmentSummary {
  id: string;
  questionCode: string | null;
  originalName: string;
  mimeType: string | null;
  byteSize: number;
  uploadedAt: string;
}

export interface QuestionnaireBundle {
  questionnaire: {
    id: string;
    year: number;
    status: QuestionnaireStatus;
    submittedAt: string | null;
    updatedAt: string;
  };
  template: Template;
  values: Answers;
  prefilled: Record<string, true>;
  attachments: AttachmentSummary[];
  progress: Progress;
  outstanding: Outstanding[];
  undecryptable: string[];
}

export interface SaveResponse {
  saved: string[];
  unknown: string[];
  progress: Progress;
  savedAt: string;
}

export interface UploadResponse {
  saved: AttachmentSummary[];
  rejected: Array<{ name: string; error: string }>;
}

export interface UploadLimits {
  maxBytes: number;
  allowedMimeTypes: string[];
}

// ---------------------------------------------------------------------------
// Firm console types
// ---------------------------------------------------------------------------

/** One row of the console grid: a client’s return for one year. */
export interface ClientGridRow {
  questionnaireId: string;
  clientId: string;
  clientCode: string | null;
  clientName: string;
  clientEmail: string;
  year: number;
  status: QuestionnaireStatus;
  assignee: string | null;
  signingPartner: string | null;
  attachmentCount: number;
  answeredCount: number;
  oneDriveUrl: string | null;
  atoStatus: AtoStatus;
  /** Dollars. The server holds cents and converts at the boundary. */
  fee: number | null;
  hours: number | null;
  hasNote: boolean;
  /** Presence only — the values never leave the server. */
  hasTfn: boolean;
  hasDob: boolean;
  submittedAt: string | null;
  updatedAt: string;
}

/** Whether the return has reached the ATO. Two states, not a workflow. */
export type AtoStatus = 'not_lodged' | 'lodged';

export interface PracticeClientsResponse {
  rows: ClientGridRow[];
  /** Years that actually have returns, so the filter offers real choices. */
  years: number[];
}

/** How far an invitation has got. Derived on the server, never stored. */
export type InvitationState = 'pending' | 'accepted' | 'expired' | 'cancelled';

export interface Invitation {
  clientUserId: string;
  clientId: string;
  name: string;
  email: string;
  invitedAt: string;
  lastLoginAt: string | null;
  /** When the most recent link stops working. Null if none was ever issued. */
  expiresAt: string | null;
  state: InvitationState;
}

export interface AddClientResponse {
  clientId: string;
  clientUserId: string;
  /** False when the address already existed and this was a resend. */
  created: boolean;
  year: number;
  /** False in development, where mail is printed rather than sent. */
  emailSent: boolean;
  expiresAt: string;
}

/**
 * What a client may see about themselves.
 *
 * There is no `tfn` field, and there is not going to be one. A client can set
 * their tax file number and replace it; they cannot read it back, so a stolen
 * session cannot extract it. `tfnHint` is the last three digits, the way a
 * card ends 4242.
 */
export interface ClientProfile {
  email: string;
  givenNames: string | null;
  familyName: string | null;
  mobile: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  hasTfn: boolean;
  tfnHint: string | null;
  hasDob: boolean;
  dob: string | null;
  /** Plain English, ready to show: "your date of birth", "your tax file number". */
  missingForFiling: string[];
}
